import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Plane, Wallet, Users, TrendingUp, Ticket, LogOut, Briefcase, ShieldCheck,
  CircleUserRound, Clock, Check, Ban, Plus, Loader2, Sliders, ReceiptText,
  Headset, Users2, RefreshCcw, BadgePercent, Landmark, LayoutDashboard,
  FileBarChart, Eye, EyeOff, Mail, PhoneCall, Building2, BedDouble, Bus as BusIcon,
  Menu, Bell, X, ChevronRight, Search, Sparkles, Wallet2, ArrowRight, Palmtree,
  Image as ImageIcon, Globe, AtSign, FileText,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import { B2BModules } from "@/components/B2BModules";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/mock";
import SideAccordion from "@/components/dashboard/SideAccordion";
import { BrandLogo } from "@/context/TenantContext";
import {
  apiListBookings, apiAdminStats, apiAdminAgents, apiApproveAgent, apiSuspendAgent,
  apiAddWallet, apiReportsSummary, apiAdminSupplierStatus, apiAdminSupplierToggle,
  apiRejectAgent, kycFileUrl, apiAdminTripjackBalance, apiAdminTripjackBalanceHistory,
} from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const TEAL = "#0E7C6B";

const BookingRow = ({ b }) => {
  const isHotel = b.type === "hotel";
  const Icon = isHotel ? BedDouble : b.type === "bus" ? BusIcon : Plane;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white p-4">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0E7C6B]/10">
          <Icon className="h-5 w-5 text-[#0E7C6B]" />
        </span>
        <div>
          <p className="text-sm font-bold text-slate-800">{b.route}</p>
          <p className="text-xs text-slate-400">
            {b.date} {b.airline ? `| ${b.airline} ${b.flightNo || ""}` : ""} | PNR: <span className="font-semibold text-slate-600">{b.pnr}</span>
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-extrabold text-[#1B1F5B]">{formatINR(b.total)}</p>
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600">
          <Check className="h-3 w-3" /> {b.status}
        </span>
      </div>
    </div>
  );
};

// ==================== AGENT PORTAL (MakeMyTrip myPartner-style) ====================
const REPORT_CHILDREN = [
  { id: "rep-flight", label: "Flight Report", icon: Plane },
  { id: "rep-hotel", label: "Hotel Report", icon: BedDouble },
  { id: "rep-bus", label: "Bus Report", icon: BusIcon },
  { id: "rep-holiday", label: "Holiday Report", icon: Palmtree },
  { id: "rep-wallet", label: "Wallet / Ledger Report", icon: ReceiptText },
];
const AGENT_NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, kind: "section" },
  { id: "book", label: "Book Now", icon: Plane, children: [
    { id: "book-flights", label: "Book Flights", icon: Plane, kind: "nav", to: "/" },
    { id: "book-hotels", label: "Book Hotels", icon: BedDouble, kind: "nav", to: "/hotel-page" },
    { id: "book-bus", label: "Book Bus", icon: BusIcon, kind: "nav", to: "/bus" },
    { id: "quotes", label: "My Quotations", icon: FileText, kind: "module" },
  ] },
  { id: "bookings", label: "All Bookings", icon: Ticket, children: [
    { id: "amendments", label: "Cancel / Reschedule", icon: RefreshCcw, kind: "module" },
  ] },
  { id: "report", label: "Report", icon: FileBarChart, children: [
    ...REPORT_CHILDREN.map((c) => ({ ...c, kind: "module" })),
    { id: "reports", label: "Sales Analytics", icon: TrendingUp, kind: "module" },
  ] },
  { id: "servicegrp", label: "Service Request", icon: Headset, children: [
    { id: "service", label: "Support Desk", icon: Headset, kind: "module" },
  ] },
  { id: "markupgrp", label: "Manage Markup", icon: Sliders, children: [
    { id: "markup", label: "My Markup", icon: Sliders, kind: "module" },
  ] },
  { id: "account", label: "My Account", icon: CircleUserRound, children: [
    { id: "statement", label: "Wallet & Ledger", icon: ReceiptText, kind: "module" },
    { id: "topup", label: "Add Money", icon: Wallet, kind: "module" },
    { id: "customers", label: "My Customers", icon: Users2, kind: "module" },
  ] },
];

const SECTION_TITLE = {
  dashboard: "Dashboard", statement: "Wallet & Ledger", topup: "Add Money to Wallet",
  reports: "Sales Analytics", markup: "Markup & Commission", customers: "My Customers", quotes: "My Quotations",
  "rep-flight": "Flight Report", "rep-hotel": "Hotel Report", "rep-bus": "Bus Report",
  "rep-holiday": "Holiday Report", "rep-wallet": "Wallet / Ledger Report",
  amendments: "Cancel / Reschedule", service: "Support Desk",
};

const AgentDashboard = ({ user, bookings, logout, navigate }) => {
  const [active, setActive] = useState("dashboard");
  const [mobileNav, setMobileNav] = useState(false);
  const [showBal, setShowBal] = useState(false);
  const totalSpend = bookings.reduce((a, b) => a + (b.total || 0), 0);
  const agentId = "FT-" + (user.user_id || "").slice(-6).toUpperCase();

  const go = (item) => {
    setMobileNav(false);
    if (item.kind === "nav") { navigate(item.to); return; }
    setActive(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const SidebarInner = () => (
    <>
      <div className="flex items-center gap-2.5 px-5 py-5">
        <span className="flex h-11 items-center justify-center rounded-xl bg-white px-2.5">
          <BrandLogo className="h-8 w-auto max-w-[140px] object-contain" testId="agent-sidebar-logo" />
        </span>
        <div>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-[#B57A00]">Partner Portal</p>
          <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">B2B Travel</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-4">
        <SideAccordion groups={AGENT_NAV} active={active} onSelect={go} testPrefix="agent" />
      </div>
      <div className="border-t border-slate-100 p-3">
        <button data-testid="agent-logout" onClick={async () => { await logout(); navigate("/"); }}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-[15px] text-slate-700 transition-colors hover:bg-red-50 hover:text-red-600">
          <LogOut className="h-[18px] w-[18px]" /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      {/* Desktop sidebar */}
      <aside className="fixed bottom-0 left-0 top-0 z-40 hidden w-72 flex-col border-r border-slate-200 bg-white lg:flex">
        <SidebarInner />
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileNav && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="agent-mobile-nav">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileNav(false)} />
          <aside className="absolute bottom-0 left-0 top-0 flex w-72 flex-col bg-white shadow-2xl">
            <button onClick={() => setMobileNav(false)} className="absolute right-3 top-3 rounded-full p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="lg:pl-72">
        {/* Sticky topbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/95 px-4 shadow-sm backdrop-blur-md md:px-6">
          <div className="flex items-center gap-3">
            <button data-testid="agent-mobile-menu" onClick={() => setMobileNav(true)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"><Menu className="h-5 w-5" /></button>
            <div>
              <h1 className="font-[Poppins] text-base font-extrabold text-slate-800 md:text-lg">{SECTION_TITLE[active] || "Dashboard"}</h1>
              <p className="hidden text-[11px] text-slate-400 sm:block">{user.agency || user.name} · {agentId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden items-center gap-2 rounded-xl border border-slate-200 bg-[#F5F7FB] px-3 py-1.5 sm:flex" data-testid="agent-wallet-chip">
              <Wallet2 className="h-4 w-4 text-[#0E7C6B]" />
              <span className="text-sm font-extrabold text-[#1B1F5B]" data-testid="agent-wallet-balance">{showBal ? formatINR(user.wallet || 0) : "₹ • • • •"}</span>
              <button data-testid="agent-wallet-toggle" onClick={() => setShowBal((s) => !s)} className="text-slate-400 hover:text-slate-700">{showBal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
            </div>
            <button data-testid="agent-add-money" onClick={() => go({ id: "topup", kind: "module" })}
              className="flex items-center gap-1.5 rounded-xl bg-[#F5B32B] px-3 py-2 text-xs font-extrabold text-[#0A0F2E] shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#E89A10] hover:shadow-md">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Add Money</span>
            </button>
            <button className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Bell className="h-5 w-5" /><span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-[#F5B32B]" /></button>
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1B1F5B] text-sm font-extrabold text-white">{user.name.charAt(0)}</span>
          </div>
        </header>

        <main className="p-4 md:p-8">
          {active === "dashboard" ? (
            <>
              {/* Announcement ticker */}
              <div className="mb-6 flex items-center gap-3 overflow-hidden rounded-xl border border-[#0E7C6B]/15 bg-[#0E7C6B]/5 px-4 py-2.5">
                <span className="flex shrink-0 items-center gap-1 rounded-md bg-[#0E7C6B] px-2 py-0.5 text-[10px] font-extrabold uppercase text-white"><Sparkles className="h-3 w-3" /> Live</span>
                <p className="truncate text-xs font-semibold text-[#0A5348]">300+ Fixed Departures · Group fares live · Instant e-ticketing via TripJack — happy selling!</p>
              </div>

              {/* Hero wallet card */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1B1F5B] via-[#252A75] to-[#0E7C6B] p-6 text-white shadow-xl md:p-8">
                <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/5" />
                <div className="absolute -bottom-16 right-24 h-48 w-48 rounded-full bg-[#F5B32B]/10" />
                <div className="relative flex flex-wrap items-end justify-between gap-6">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-[#F5B32B]">Welcome back</p>
                    <h2 className="mt-1 font-[Poppins] text-2xl font-extrabold md:text-3xl">{user.agency || user.name}</h2>
                    <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold text-white/80"><Briefcase className="h-3 w-3" /> Agent ID · {agentId}</span>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Wallet Balance</p>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-3xl font-extrabold">{showBal ? formatINR(user.wallet || 0) : "₹ • • • • • •"}</p>
                      <button data-testid="agent-wallet-toggle-hero" onClick={() => setShowBal((s) => !s)} className="text-white/50 hover:text-white">{showBal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => go({ id: "statement", kind: "module" })} className="flex-1 rounded-lg border border-white/25 px-3 py-1.5 text-[11px] font-bold hover:bg-white/10">Statement</button>
                      <button onClick={() => go({ id: "topup", kind: "module" })} className="flex-1 rounded-lg bg-[#F5B32B] px-3 py-1.5 text-[11px] font-extrabold text-[#0A0F2E] hover:bg-[#E89A10]">+ Add Money</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product tiles */}
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  { label: "Book Flights", sub: "Live TripJack fares", icon: Plane, to: "/", tint: "from-sky-500 to-blue-600" },
                  { label: "Book Hotels", sub: "Worldwide inventory", icon: BedDouble, to: "/hotel-page", tint: "from-emerald-500 to-teal-600" },
                  { label: "Book Bus", sub: "Comfortable journeys", icon: BusIcon, to: "/bus", tint: "from-amber-500 to-orange-600" },
                ].map((p) => {
                  const Icon = p.icon;
                  return (
                    <button key={p.label} data-testid={`product-${p.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => navigate(p.to)}
                      className="group flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                      <span className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${p.tint} text-white shadow-sm`}><Icon className="h-6 w-6" /></span>
                      <div className="flex-1">
                        <p className="font-bold text-slate-800">{p.label}</p>
                        <p className="text-xs text-slate-400">{p.sub}</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-[#0E7C6B]" />
                    </button>
                  );
                })}
              </div>

              {/* KPI cards */}
              <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {[
                  { icon: Wallet, label: "Wallet Balance", value: formatINR(user.wallet || 0), tint: "bg-[#0E7C6B]/10 text-[#0E7C6B]" },
                  { icon: Ticket, label: "Total Bookings", value: bookings.length, tint: "bg-[#F5B32B]/15 text-[#B57A00]" },
                  { icon: TrendingUp, label: "Total Spend", value: formatINR(totalSpend), tint: "bg-emerald-50 text-emerald-600" },
                  { icon: CircleUserRound, label: "Status", value: user.status, tint: "bg-violet-50 text-violet-600" },
                ].map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.tint}`}><Icon className="h-5 w-5" /></span>
                      <p className="mt-3 text-lg font-extrabold capitalize text-slate-800">{s.value}</p>
                      <p className="text-[11px] font-semibold uppercase text-slate-400">{s.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Recent bookings */}
              <div className="mt-8">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-extrabold text-slate-800">Recent Bookings</h2>
                  {bookings.length > 0 && <span className="text-xs font-semibold text-slate-400">{bookings.length} total</span>}
                </div>
                <div className="space-y-3">
                  {bookings.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center">
                      <Plane className="mx-auto h-9 w-9 text-slate-300" />
                      <p className="mt-2 text-sm font-semibold text-slate-500">No bookings yet</p>
                      <button onClick={() => navigate("/")} className="mt-3 inline-block rounded-lg bg-[#0E7C6B] px-5 py-2 text-sm font-bold text-white hover:bg-[#0A5348]">Book Now</button>
                    </div>
                  ) : bookings.slice(0, 8).map((b) => <BookingRow key={b.pnr} b={b} />)}
                </div>
              </div>
            </>
          ) : (
            <div data-testid={`agent-section-${active}`}>
              <B2BModules role="agent" tab={active} setTab={(t) => setActive(t)} hideTabs />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

// ==================== ADMIN DASHBOARD (flytrip style w/ sidebar) ====================
const ADMIN_MENU = [
  { id: "dash", label: "Dashboard", icon: LayoutDashboard, scroll: "top" },
  { id: "bk", label: "All Bookings", icon: Ticket, children: [
    { id: "allbookings", label: "All Bookings", icon: Ticket, tab: "allbookings" },
    { id: "amendments", label: "Amendment Tracking", icon: RefreshCcw, tab: "amendments" },
    { id: "queries", label: "Queries Hub", icon: Mail, tab: "queries" },
  ] },
  { id: "rep", label: "Report", icon: FileBarChart, children: [
    ...REPORT_CHILDREN.map((c) => ({ ...c, tab: c.id })),
    { id: "reports", label: "Agent Sales Summary", icon: TrendingUp, tab: "reports" },
  ] },
  { id: "sr", label: "Service Request", icon: Headset, children: [
    { id: "service", label: "Service Requests", icon: Headset, tab: "service" },
    { id: "deposits", label: "Top-up Approvals", icon: Wallet, tab: "deposits" },
    { id: "walletloads", label: "Wallet Loads", icon: Wallet, tab: "walletloads" },
  ] },
  { id: "mk", label: "Manage Markup", icon: Sliders, children: [
    { id: "mk-flight", label: "Flight Setting", icon: Plane, tab: "mk-flight" },
    { id: "mk-hotel", label: "Hotel Setting", icon: BedDouble, tab: "mk-hotel" },
    { id: "mk-bus", label: "Bus Setting", icon: BusIcon, tab: "mk-bus" },
    { id: "offers", label: "Manage Coupons", icon: BadgePercent, tab: "offers" },
    { id: "mk-overview", label: "B2C Markup", icon: Sliders, tab: "mk-overview" },
    { id: "markup", label: "Agent Markup", icon: Users, tab: "markup" },
  ] },
  { id: "agents", label: "Agents", icon: Users, scroll: "agents" },
  { id: "hol", label: "Holidays", icon: Palmtree, children: [
    { id: "holidays", label: "Holiday Packages", icon: Palmtree, tab: "holidays" },
    { id: "enquiries", label: "Holiday Enquiries", icon: Mail, tab: "enquiries" },
  ] },
  { id: "web", label: "Website", icon: Globe, children: [
    { id: "website", label: "Website Banners", icon: ImageIcon, tab: "website" },
    { id: "seo", label: "SEO Management", icon: Globe, tab: "seo" },
    { id: "subscribers", label: "Subscribers", icon: AtSign, tab: "subscribers" },
  ] },
  { id: "acc", label: "My Account", icon: CircleUserRound, children: [
    { id: "finance", label: "Finance", icon: ReceiptText, tab: "finance" },
    { id: "banks", label: "Bank Details", icon: Landmark, tab: "banks" },
    { id: "logout", label: "Logout", icon: LogOut },
  ] },
];

const AdminDashboard = ({ user, bookings, stats, agents, reloadAgents, onWallet }) => {
  const [tab, setTab] = useState("deposits");
  const [active, setActive] = useState("dash");
  const [report, setReport] = useState(null);
  const topRef = useRef(null), agentsRef = useRef(null), bookRef = useRef(null), modRef = useRef(null);
  useEffect(() => { apiReportsSummary().then(setReport).catch(() => {}); }, []);

  const { logout: adminLogout } = useAuth();
  const nav = async (item) => {
    if (item.id === "logout") { await adminLogout(); window.location.href = "/"; return; }
    setActive(item.id);
    if (item.tab) { setTab(item.tab); setTimeout(() => modRef.current?.scrollIntoView({ behavior: "smooth" }), 60); }
    else if (item.scroll === "agents") agentsRef.current?.scrollIntoView({ behavior: "smooth" });
    else if (item.scroll === "bookings") bookRef.current?.scrollIntoView({ behavior: "smooth" });
    else topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const approve = async (id) => { await apiApproveAgent(id); toast.success("Agent approved"); setKycAgent(null); reloadAgents(); };
  const suspend = async (id) => { await apiSuspendAgent(id); toast.success("Agent suspended"); reloadAgents(); };
  const [kycAgent, setKycAgent] = useState(null);
  const [rejReason, setRejReason] = useState("");
  const [tjBal, setTjBal] = useState(null);
  const [tjLoading, setTjLoading] = useState(false);
  const [tjHistory, setTjHistory] = useState([]);
  const loadTjBal = async () => {
    setTjLoading(true);
    try { setTjBal(await apiAdminTripjackBalance()); }
    catch { setTjBal({ error: "Could not fetch balance" }); }
    finally { setTjLoading(false); }
  };
  useEffect(() => {
    loadTjBal();
    apiAdminTripjackBalanceHistory().then((d) => setTjHistory(d.history || [])).catch(() => {});
  }, []);
  const reject = async (id) => {
    await apiRejectAgent(id, rejReason);
    toast.success("Agent rejected");
    setKycAgent(null); setRejReason(""); reloadAgents();
  };

  const heroTiles = [
    { label: "Flights", value: report?.flights ?? 0 },
    { label: "Hotels", value: report?.hotels ?? 0 },
    { label: "Bookings", value: report?.total ?? stats?.bookings ?? 0 },
    { label: "Revenue", value: formatINR(report?.revenue ?? stats?.revenue ?? 0) },
    { label: "Agents", value: stats?.agents ?? 0 },
  ];
  const statCards = [
    { label: "Total Users", value: stats?.total_users ?? 0, tint: "bg-[#0E7C6B]/10 text-[#0E7C6B]", icon: Users },
    { label: "Total Bookings", value: report?.total ?? 0, tint: "bg-[#F5B32B]/15 text-[#B57A00]", icon: Ticket },
    { label: "Total Revenue", value: formatINR(report?.revenue ?? 0), tint: "bg-emerald-50 text-emerald-600", icon: TrendingUp },
    { label: `Pending Agents`, value: stats?.pending_agents ?? 0, tint: "bg-amber-50 text-amber-600", icon: Briefcase },
    { label: "Flights Sold", value: report?.flights ?? 0, tint: "bg-sky-50 text-sky-600", icon: Plane },
    { label: "Hotels Sold", value: report?.hotels ?? 0, tint: "bg-violet-50 text-violet-600", icon: BedDouble },
  ];

  // Charts from real bookings
  const bType = (b) => b.type || (b.flight ? "flight" : b.hotel ? "hotel" : "flight");
  const monthMap = {};
  bookings.forEach((b) => {
    const mk = (b.created_at || "").slice(0, 7);
    if (!mk) return;
    monthMap[mk] = (monthMap[mk] || 0) + (b.total || 0);
  });
  const monthlyData = Object.keys(monthMap).sort().slice(-12).map((m) => ({
    month: m.slice(5) + "/" + m.slice(2, 4), revenue: monthMap[m],
  }));
  const splitRaw = { flight: 0, hotel: 0, bus: 0 };
  bookings.forEach((b) => { splitRaw[bType(b)] = (splitRaw[bType(b)] || 0) + 1; });
  const SPLIT_COLORS = { flight: "#1B1F5B", hotel: "#0E7C6B", bus: "#F5B32B" };
  const splitData = Object.entries(splitRaw).filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: SPLIT_COLORS[k] }));

  // Supplier booking readiness (auto-blocks payments when TripJack fails)
  const [suppliers, setSuppliers] = useState([]);
  const loadSuppliers = () => apiAdminSupplierStatus().then((d) => setSuppliers(d.suppliers || [])).catch(() => {});
  useEffect(() => { loadSuppliers(); }, []);
  const supplierState = (p) => {
    const s = suppliers.find((x) => x.product === p);
    if (!s) return { off: false, note: "Live" };
    if (s.manual_off) return { off: true, note: "Manually OFF" };
    if (s.blocked_until && new Date(s.blocked_until) > new Date()) return { off: true, note: "Auto-blocked (supplier issue)" };
    return { off: false, note: "Live" };
  };

  return (
    <div ref={topRef} className="mx-auto flex max-w-[1400px] gap-5 px-4 py-6">
      {/* Sidebar */}
      <aside className="sticky top-4 hidden h-fit w-64 shrink-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm lg:block">
        <p className="px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-[#B57A00]">Admin Portal</p>
        <SideAccordion groups={ADMIN_MENU} active={active} onSelect={nav} testPrefix="admin" />
      </aside>

      {/* Main */}
      <div className="min-w-0 flex-1">
        {/* Lifetime sales hero */}
        <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-[#1B1F5B] to-[#2A2F7A] p-6 text-white shadow-lg">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#F5B32B]">Lifetime Sales</p>
          <h1 className="mt-1 font-[Poppins] text-4xl font-extrabold">{formatINR(report?.revenue ?? stats?.revenue ?? 0)}</h1>
          <p className="text-xs text-white/60">All time · Flights + Hotels</p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {heroTiles.map((t) => (
              <div key={t.label} className="rounded-xl bg-white/10 px-3 py-2.5">
                <p className="text-lg font-extrabold">{t.value}</p>
                <p className="text-[10px] uppercase tracking-wide text-white/60">{t.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stat cards */}
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.tint}`}><Icon className="h-5 w-5" /></span>
                <p className="mt-2 text-lg font-extrabold text-slate-800">{s.value}</p>
                <p className="text-[10px] font-semibold uppercase text-slate-400">{s.label}</p>
              </div>
            );
          })}
        </div>

        {/* TripJack live account balance */}
        <div className="mt-6 rounded-2xl border border-[#1B1F5B]/15 bg-gradient-to-br from-[#1B1F5B]/[0.04] to-white p-5 shadow-sm" data-testid="tripjack-balance-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-extrabold text-slate-800">TripJack Supplier Balance (Live)</h3>
              <p className="text-xs text-slate-400">Direct from TripJack User Detail API {tjBal?.userId ? `· A/C ${tjBal.userId}` : ""}</p>
            </div>
            <button data-testid="tripjack-balance-refresh" onClick={loadTjBal} disabled={tjLoading}
              className="flex items-center gap-1.5 rounded-lg bg-[#1B1F5B] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#141844] disabled:opacity-60">
              {tjLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />} Refresh
            </button>
          </div>
          {tjBal?.low && !tjBal?.error && (
            <div data-testid="tripjack-low-alert" className="mt-3 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-600">
              <Ban className="h-4 w-4" /> Wallet {formatINR(tjBal.walletBalance || 0)} is below the {formatINR(tjBal.low_threshold || 0)} threshold — top up TripJack to avoid booking failures.
            </div>
          )}
          {tjBal?.error ? (
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-500" data-testid="tripjack-balance-error">{tjBal.error}</p>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { label: "Wallet Balance", value: tjBal?.walletBalance, key: "wallet", accent: "text-[#0E7C6B]" },
                { label: "Credit Balance", value: tjBal?.creditBalance, key: "credit", accent: "text-[#1B1F5B]" },
                { label: "Total Balance", value: tjBal?.totalBalance, key: "total", accent: "text-[#1B1F5B]" },
                { label: "Outstanding", value: tjBal?.totalOutstanding, key: "outstanding", accent: "text-[#B57A00]" },
              ].map((s) => (
                <div key={s.key} data-testid={`tripjack-${s.key}`} className="rounded-xl border border-slate-100 bg-white px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase text-slate-400">{s.label}</p>
                  <p className={`text-lg font-extrabold ${s.accent}`}>{tjLoading ? "…" : formatINR(s.value || 0)}</p>
                </div>
              ))}
            </div>
          )}
          {tjHistory.length > 1 && (
            <div className="mt-5" data-testid="tripjack-balance-chart">
              <p className="mb-2 text-[10px] font-bold uppercase text-slate-400">Wallet Balance — Daily Snapshot</p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={tjHistory.map((h) => ({ date: (h.date || "").slice(5), wallet: h.walletBalance }))}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={50} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Bar dataKey="wallet" radius={[4, 4, 0, 0]}>
                    {tjHistory.map((h, i) => (
                      <Cell key={i} fill={h.walletBalance < (tjBal?.low_threshold || 25000) ? "#EF4444" : "#0E7C6B"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Supplier readiness controls */}
        <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="supplier-status-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-extrabold text-slate-800">Supplier Booking Readiness</h3>
              <p className="text-xs text-slate-400">Jab TripJack wallet/balance issue ho to payment auto-block hota hai. Top-up ke baad yahan se ON karein.</p>
            </div>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[{ p: "flight", label: "Flight Bookings" }, { p: "hotel", label: "Hotel Bookings" }].map(({ p, label }) => {
              const st = supplierState(p);
              return (
                <div key={p} data-testid={`supplier-${p}-row`} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
                  <div>
                    <p className="text-sm font-bold text-slate-700">{label}</p>
                    <p className={`text-[11px] font-semibold ${st.off ? "text-amber-600" : "text-emerald-600"}`}>{st.note}</p>
                  </div>
                  <button data-testid={`supplier-${p}-toggle`}
                    onClick={async () => { try { await apiAdminSupplierToggle(p, !st.off); toast.success(`${label} ${st.off ? "enabled" : "disabled"}`); loadSuppliers(); } catch { toast.error("Could not update"); } }}
                    className={`rounded-full px-4 py-1.5 text-xs font-extrabold ${st.off ? "bg-amber-500 text-white" : "bg-emerald-500 text-white"}`}>
                    {st.off ? "BLOCKED" : "ACTIVE"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Charts */}
        <div className="mt-6 grid gap-4 lg:grid-cols-3" data-testid="admin-charts">
          <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm lg:col-span-2">
            <h3 className="text-sm font-extrabold text-slate-800">Revenue Statistic</h3>
            <p className="text-xs text-slate-400">Monthly booking revenue (last 12 months)</p>
            <div className="mt-4 h-64">
              {monthlyData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-400">No booking data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatINR(v)} cursor={{ fill: "#1B1F5B0d" }} />
                    <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="#1B1F5B" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-extrabold text-slate-800">Product Split</h3>
            <p className="text-xs text-slate-400">Bookings by category</p>
            <div className="mt-4 h-64">
              {splitData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-400">No booking data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={splitData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                      {splitData.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <Tooltip />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Agents */}
        <div ref={agentsRef} className="mt-8">
          <h2 className="text-lg font-extrabold text-slate-800">Agent Management</h2>
          <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-sm">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-3">Agency / Owner</th><th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Wallet</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {agents.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">No agents yet</td></tr>}
                {agents.map((a) => (
                  <tr key={a.user_id} className="border-b last:border-0">
                    <td className="px-5 py-3"><p className="font-bold text-slate-700">{a.agency || "-"}</p><p className="text-xs text-slate-400">{a.name}</p>
                      {a.agency_site && a.agency_site !== "FLYTRIP" && <span className="mt-0.5 inline-block rounded bg-[#F5B32B]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#B57A00]" data-testid={`agent-site-${a.user_id}`}>via {a.agency_site}</span>}</td>
                    <td className="px-5 py-3 text-slate-500">{a.email}</td>
                    <td className="px-5 py-3 font-bold text-[#1B1F5B]">{formatINR(a.wallet || 0)}</td>
                    <td className="px-5 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${a.status === "active" ? "bg-emerald-50 text-emerald-600" : a.status === "pending" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-500"}`}>{a.status}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {a.kyc && <button data-testid={`kyc-view-${a.user_id}`} onClick={() => { setKycAgent(a); setRejReason(""); }} className="flex items-center gap-1 rounded-lg bg-[#0E7C6B] px-2.5 py-1.5 text-[11px] font-bold text-white"><FileBarChart className="h-3 w-3" />View KYC</button>}
                        {a.status !== "active" && <button data-testid={`agent-approve-${a.user_id}`} onClick={() => approve(a.user_id)} className="flex items-center gap-1 rounded-lg bg-emerald-500 px-2.5 py-1.5 text-[11px] font-bold text-white"><Check className="h-3 w-3" />{a.status === "pending" ? "Approve" : "Reactivate"}</button>}
                        {a.status === "active" && <button onClick={() => suspend(a.user_id)} className="flex items-center gap-1 rounded-lg bg-red-500 px-2.5 py-1.5 text-[11px] font-bold text-white"><Ban className="h-3 w-3" />Suspend</button>}
                        <button onClick={() => onWallet(a)} className="flex items-center gap-1 rounded-lg bg-[#1B1F5B] px-2.5 py-1.5 text-[11px] font-bold text-white"><Plus className="h-3 w-3" />Wallet</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* KYC Review Dialog */}
        <Dialog open={!!kycAgent} onOpenChange={() => setKycAgent(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="kyc-review-dialog">
            <DialogHeader><DialogTitle>KYC Verification — {kycAgent?.agency || kycAgent?.name}</DialogTitle></DialogHeader>
            {kycAgent && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {[
                    ["Agency", kycAgent.agency], ["Contact", kycAgent.name],
                    ["Email", kycAgent.email], ["Phone", kycAgent.phone],
                    ["Business", kycAgent.kyc?.businessType], ["PAN", kycAgent.kyc?.pan],
                    ["GST", kycAgent.kyc?.gst || "—"],
                    ["Address", `${kycAgent.kyc?.address || ""} ${kycAgent.kyc?.city || ""} ${kycAgent.kyc?.state || ""} ${kycAgent.kyc?.pincode || ""}`.trim() || "—"],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <p className="text-[10px] font-bold uppercase text-slate-400">{k}</p>
                      <p className="font-semibold text-slate-700">{v || "—"}</p>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="mb-2 text-[10px] font-bold uppercase text-slate-400">Documents</p>
                  <div className="flex flex-wrap gap-2">
                    {(kycAgent.kyc?.documents || []).map((d) => (
                      <a key={d.file_id} data-testid={`kyc-doc-${d.doc_type}`} href={kycFileUrl(d.file_id)} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-[#1B1F5B] hover:border-[#1B1F5B]">
                        <FileBarChart className="h-4 w-4" /> {d.doc_type.replace("_", " ")}
                      </a>
                    ))}
                    {(!kycAgent.kyc?.documents || kycAgent.kyc.documents.length === 0) && <p className="text-xs text-slate-400">No documents uploaded</p>}
                  </div>
                </div>
                {kycAgent.kyc_status === "rejected" && kycAgent.kyc_reject_reason && (
                  <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-500">Last rejection: {kycAgent.kyc_reject_reason}</p>
                )}
                <textarea data-testid="kyc-reject-reason" value={rejReason} onChange={(e) => setRejReason(e.target.value)}
                  placeholder="Reason (required if rejecting)" rows={2}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#1B1F5B]" />
                <div className="flex gap-2">
                  <button data-testid="kyc-reject-btn" onClick={() => rejReason.trim() ? reject(kycAgent.user_id) : toast.error("Please add a rejection reason")}
                    className="flex-1 rounded-xl border border-red-200 py-2.5 text-sm font-bold text-red-500 hover:bg-red-50">Reject</button>
                  <button data-testid="kyc-approve-btn" onClick={() => approve(kycAgent.user_id)}
                    className="flex-1 rounded-xl bg-emerald-500 py-2.5 text-sm font-extrabold text-white hover:bg-emerald-600">Approve & Activate</button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modules */}
        <div ref={modRef} className="mt-8">
          <h2 className="text-lg font-extrabold text-slate-800">Admin Modules</h2>
          <B2BModules role="admin" tab={tab} setTab={setTab} mobileTabsOnly />
        </div>

        {/* Bookings */}
        <div ref={bookRef} className="mt-8">
          <h2 className="text-lg font-extrabold text-slate-800">Recent Bookings (All)</h2>
          <div className="mt-3 space-y-3">
            {bookings.length === 0 ? <p className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center text-sm text-slate-400">No bookings yet</p>
              : bookings.map((b) => <BookingRow key={b.pnr} b={b} />)}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CUSTOMER DASHBOARD ====================
const CustomerDashboard = ({ user, bookings }) => {
  const totalSpend = bookings.reduce((a, b) => a + (b.total || 0), 0);
  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { icon: Ticket, label: "My Bookings", value: bookings.length, tint: "bg-[#0E7C6B]/10 text-[#0E7C6B]" },
          { icon: Wallet, label: "Total Spend", value: formatINR(totalSpend), tint: "bg-[#F5B32B]/15 text-[#B57A00]" },
          { icon: TrendingUp, label: "Reward Points", value: bookings.length * 120, tint: "bg-emerald-50 text-emerald-600" },
          { icon: Clock, label: "Trips Taken", value: bookings.length, tint: "bg-violet-50 text-violet-600" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.tint}`}><Icon className="h-5 w-5" /></span>
              <div><p className="text-[11px] font-semibold uppercase text-slate-400">{s.label}</p><p className="text-lg font-extrabold text-slate-800">{s.value}</p></div>
            </div>
          );
        })}
      </div>
      <div className="mt-8">
        <h2 className="text-lg font-extrabold text-slate-800">My Bookings</h2>
        <div className="mt-3 space-y-3">
          {bookings.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center">
              <Plane className="mx-auto h-9 w-9 text-slate-300" />
              <p className="mt-2 text-sm font-semibold text-slate-500">No bookings yet</p>
              <Link to="/" className="mt-3 inline-block rounded-lg bg-[#0E7C6B] px-5 py-2 text-sm font-bold text-white">Book a Flight</Link>
            </div>
          ) : bookings.map((b) => <BookingRow key={b.pnr} b={b} />)}
        </div>
      </div>
    </div>
  );
};

// ==================== SHELL ====================
const Dashboard = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [walletAgent, setWalletAgent] = useState(null);
  const [walletAmt, setWalletAmt] = useState("");
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role === "tenant_admin") { navigate("/agency-admin"); return; }
    (async () => {
      setDataLoading(true);
      try {
        const bk = await apiListBookings();
        setBookings(bk.bookings || []);
        if (user.role === "admin") {
          const [s, a] = await Promise.all([apiAdminStats(), apiAdminAgents()]);
          setStats(s); setAgents(a.agents || []);
        }
      } catch { /* ignore */ } finally { setDataLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const reloadAgents = async () => {
    const a = await apiAdminAgents(); setAgents(a.agents || []);
    const s = await apiAdminStats(); setStats(s);
  };

  const addWallet = async () => {
    const amt = parseInt(walletAmt, 10);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    try {
      await apiAddWallet(walletAgent.user_id, amt, "Admin top-up");
      toast.success(`Added ${formatINR(amt)} to ${walletAgent.name}`);
      setWalletAgent(null); setWalletAmt(""); reloadAgents();
    } catch { toast.error("Could not add balance"); }
  };

  if (loading || !user) return null;

  // Agent gets the full-screen MMT-style portal shell (own sidebar + topbar)
  if (user.role === "agent") {
    if (dataLoading) return <div className="flex min-h-screen items-center justify-center bg-[#F5F7FB]"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;
    return <AgentDashboard user={user} bookings={bookings} logout={logout} navigate={navigate} />;
  }

  const roleLabel = user.role === "admin" ? "Admin Portal" : user.role === "agent" ? "B2B Agent Portal" : "My Account";
  const RoleIcon = user.role === "admin" ? ShieldCheck : user.role === "agent" ? Briefcase : CircleUserRound;

  return (
    <div className="min-h-screen bg-[#F1F5F4]">
      <TopBar />
      <Header />
      {/* Slim profile bar */}
      <div className="border-b border-slate-100 bg-white">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            {user.picture ? <img src={user.picture} alt="" className="h-10 w-10 rounded-xl object-cover" />
              : <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F5B32B] text-lg font-extrabold text-[#0A0F2E]">{user.name.charAt(0)}</span>}
            <div>
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#0E7C6B]"><RoleIcon className="h-3 w-3" /> {roleLabel}</p>
              <p className="text-sm font-extrabold text-slate-800">{user.name} {user.agency ? <span className="font-normal text-slate-400">· {user.agency}</span> : ""}</p>
            </div>
          </div>
          <button data-testid="dashboard-header-logout" onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      {dataLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>
      ) : user.role === "admin" ? (
        <>
        <Link to="/admin/tenants" data-testid="admin-tenants-link"
          className="mb-6 flex items-center justify-between rounded-2xl bg-[#1B1F5B] px-6 py-4 text-white hover:bg-[#141844]">
          <span><b>White-label Agencies</b><span className="ml-2 text-sm text-white/70">Manage tenant domains, branding & isolated bookings</span></span>
          <span className="text-[#F5B32B]">Open →</span>
        </Link>
        <AdminDashboard user={user} bookings={bookings} stats={stats} agents={agents} reloadAgents={reloadAgents} onWallet={setWalletAgent} />
        </>
      ) : (
        <CustomerDashboard user={user} bookings={bookings} />
      )}

      <Dialog open={!!walletAgent} onOpenChange={() => setWalletAgent(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Add Wallet Balance</DialogTitle></DialogHeader>
          {walletAgent && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Agent: <span className="font-bold text-slate-800">{walletAgent.name}</span> ({walletAgent.agency || "-"})<br />Current: <span className="font-bold text-[#1B1F5B]">{formatINR(walletAgent.wallet || 0)}</span></p>
              <input type="number" value={walletAmt} onChange={(e) => setWalletAmt(e.target.value)} placeholder="Amount (INR)" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <button onClick={addWallet} className="w-full rounded-lg bg-[#0E7C6B] py-2.5 text-sm font-bold text-white hover:bg-[#0A5348]">Add Balance</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Dashboard;
